import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, ProductContextModel, withProduct } from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ArrowLeft from "@insite/mobius/Icons/ArrowLeft";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ExplodedViewItemsComponent from "@store/Components/ExplodedView/ExplodedViewItems";
import { arrowButton } from "@store/Extensions/Widgets/Common/ProductCarousel";
import usePrevious from "@store/Hooks/UsePrevious";
import useVisibility from "@store/Hooks/UseVisibility";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { StorePdpState } from "@store/Store/Reducers/PdpReducer";
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const ExplodedViewNavigatorComponent = React.lazy(() => import("@store/Components/ExplodedView/ExplodedViewNavigator"));

const mapStateToProps = (state: StorePdpState) => ({
    evDrawings: getProductsForProductInfoList(state, "explodedViewsListItems"),
    evDrawingItemsList: getProductsForProductInfoList(state, "explodedViewItems"),
});

const mapDispatchToProps = {
    loadCarouselProducts,
    loadProductInfoList,
};

type Props = ThemeProps<BaseTheme> &
    HasShellContext &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface SubAssemblyItemsStyles {
    errorMessageText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
}

export const specificationsStyles: SubAssemblyItemsStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    wrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 100%;
                max-width: 100%;
            `,
        },
        image: {
            css: css`
                img {
                    height: auto;
                    max-width: 100%;
                }
            `,
        },
        img: {
            css: css`
                height: auto;
                width: 100%;
                max-width: 100%;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin: 0;
                font-weight: ${TYPOGRAPHY.weight.black};
                font-size: ${TYPOGRAPHY.size.large};
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin: 0;
            `,
        },
        erpNumberGridItem: {
            css: css`
                justify-content: left;
            `,
        },
        erpNumberValueText: {
            size: 13,
            css: css`
                flex: 0 0 auto;
                margin-left: 4px;
            `,
        },
        erpNumberLabelText: { size: 13 },
    },
    mainContainer: {
        gap: 0,
        css: css`
            background: ${COLORS.background.alt};
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            flex-basis: 5%;
            max-width: 5%;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            ${arrowButton()}
            border: 0;
            padding: 0;
        `,
        icon: {
            src: ArrowLeft,
            position: "left",
        },
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 5%;
            max-width: 5%;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            ${arrowButton()}
            border: 0;
            padding: 0;
        `,
        icon: {
            src: ArrowRight,
            position: "left",
        },
    },
    carouselGridItem: {
        width: 10,
        css: css`
            flex-basis: 90%;
            max-width: 90%;
        `,
    },
    carouselWrapper: {
        css: css`
            position: relative;
            max-width: 100%;
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            @keyframes wait {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 0;
                }
            }

            @keyframes fadeIn {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
            overflow: hidden;
            max-width: 100%;
            width: 100%;
            opacity: 1;
            animation: wait 250ms, fadeIn 400ms 250ms;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            width: calc(100% + 15px);
            background: ${COLORS.background.alt};
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            margin-left: 7px;
            margin-right: 7.5px;
            min-width: 245px;
            border: 5px solid ${COLORS.background.alt}; ;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            background: white;
            padding: 8px;
        `,
    },
    selectButton: {
        css: css`
            margin-top: 30px;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const styles = specificationsStyles;

const ExplodedViewsList: React.FC<Props> = ({
    shellContext,
    product,
    evDrawings,
    evDrawingItemsList,
    loadProductInfoList,
    theme,
}) => {
    if (IS_SERVER_SIDE) {
        return null;
    }
    const prevEvDrawings = usePrevious(evDrawings);
    const numberOfProductsToDisplay = 4;
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const { isInViewport, ref } = useVisibility();
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };
    const [subExplodedViewToUse, setSubExplodedViewToUse] = React.useState<ProductModel | undefined>(undefined);

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = numberOfProductsToDisplay;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = numberOfProductsToDisplay;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, []);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const handleClick = (
        e: React.MouseEvent<Element, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        index: number,
    ) => {
        e.preventDefault();
        if (!evDrawings) {
            return;
        }
        setSubExplodedViewToUse(evDrawings[index]);
    };

    React.useEffect(() => {
        if (prevEvDrawings === undefined && evDrawings && evDrawings.length === 1) {
            // Loading Products
            loadProductInfoList({
                id: "explodedViewItems",
                getProductCollectionParameter: {
                    productId: evDrawings[0]?.id,
                    relationship: "ExplodedViewProduct",
                    pageSize: 999,
                    expand: ["images", "badges"],
                },
                forceLoad: true,
            });
        }
    }, [evDrawings]);

    React.useEffect(() => {
        // Loading EV Drawings
        loadProductInfoList({
            id: "explodedViewsListItems",
            getProductCollectionParameter: {
                productId: product?.id,
                relationship: "ExplodedViewDrawing",
                pageSize: 999,
                expand: ["images", "badges"],
            },
            forceLoad: true,
        });
    }, [product?.id]);

    React.useEffect(() => {
        if (!subExplodedViewToUse || !product) {
            return;
        }
        if (subExplodedViewToUse) {
            // Loading Products
            loadProductInfoList({
                id: "explodedViewItems",
                getProductCollectionParameter: {
                    productId: subExplodedViewToUse?.id,
                    relationship: "ExplodedViewProduct",
                    pageSize: 999,
                    expand: ["images", "badges"],
                },
                forceLoad: true,
            });
        }
    }, [subExplodedViewToUse]);

    React.useEffect(() => {
        if (evDrawings && evDrawings.length === 1) {
            return;
        }
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [windowResizeTime]);

    React.useEffect(() => {
        if (evDrawings && evDrawings.length === 1) {
            return;
        }
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (evDrawings && evDrawings.length === 1) {
            return;
        }
        if (!embla) {
            return;
        }

        // Manage display flicker since the slider is initially not in the dom
        setTimeout(() => {
            if (!evDrawings) {
                return;
            }
            embla.reInit({
                align: "start",
                slidesToScroll,
                draggable: true,
                loop: evDrawings.length > slidesToScroll,
            });
            setCanScroll();
        }, 250);
    }, [embla, slidesToScroll]);

    if (!evDrawings || !product || !evDrawings[0] || !evDrawingItemsList) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {translate("Cannot display exploded view navigator in content admin.")}
            </Typography>
        );
    }

    return (
        <StyledWrapper ref={ref}>
            {isInViewport && (
                <>
                    {evDrawings.length > 1 && (
                        <>
                            <GridContainer {...styles.mainContainer}>
                                <GridItem {...styles.prevArrowGridItem}>
                                    {evDrawings.length > slidesToScroll && (
                                        <Button
                                            {...styles.prevArrowButton}
                                            onClick={() => embla && embla.scrollPrev()}
                                            disabled={!canScrollPrev}
                                            data-test-selector="prevBtn"
                                        >
                                            <ButtonIcon src={ChevronLeft} />
                                        </Button>
                                    )}
                                </GridItem>
                                <GridItem {...styles.carouselGridItem}>
                                    <StyledWrapper {...styles.carouselWrapper}>
                                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                            <StyledWrapper
                                                {...styles.carouselSlidesContainer}
                                                data-test-selector="slides"
                                            >
                                                {evDrawings.length > 1 &&
                                                    evDrawings.map((subExplodedView, index) => {
                                                        const productContext: ProductContextModel = {
                                                            product: subExplodedView,
                                                            productInfo: {
                                                                productId: "",
                                                                qtyOrdered: 0,
                                                                unitOfMeasure: "",
                                                                productDetailPath: "",
                                                            },
                                                        };
                                                        return (
                                                            <StyledWrapper
                                                                key={subExplodedView.id}
                                                                {...styles.carouselSlide}
                                                                style={{
                                                                    width: `calc(100% / ${slidesToScroll} - 15px)`,
                                                                }}
                                                            >
                                                                <StyledWrapper {...styles.carouselSlideInner}>
                                                                    <ProductImage
                                                                        onClick={e => {
                                                                            handleClick(e, index);
                                                                        }}
                                                                        extendedStyles={styles.productImage}
                                                                        product={productContext}
                                                                    />
                                                                    <ProductDescription
                                                                        product={productContext}
                                                                        extendedStyles={styles.productDescriptionStyles}
                                                                        onClick={e => {
                                                                            handleClick(e, index);
                                                                        }}
                                                                    />
                                                                    {subExplodedView.productNumber}
                                                                    <ProductPartNumbers
                                                                        productNumber={subExplodedView.productNumber}
                                                                        customerProductNumber={
                                                                            subExplodedView.customerProductNumber
                                                                        }
                                                                        manufacturerItem={
                                                                            subExplodedView.manufacturerItem
                                                                        }
                                                                        showCustomerName={false}
                                                                        showManufacturerItem={false}
                                                                        showLabel={true}
                                                                        extendedStyles={styles.productPartNumbersStyles}
                                                                    />
                                                                    <Button
                                                                        {...styles.selectButton}
                                                                        disabled={
                                                                            subExplodedViewToUse?.id ===
                                                                            subExplodedView.id
                                                                        }
                                                                        onClick={e => {
                                                                            handleClick(e, index);
                                                                        }}
                                                                    >
                                                                        {translate("View Details")}
                                                                    </Button>
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        );
                                                    })}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.nextArrowGridItem}>
                                    {evDrawings.length > slidesToScroll && (
                                        <Button
                                            {...styles.nextArrowButton}
                                            onClick={() => embla && embla.scrollNext()}
                                            disabled={!canScrollNext}
                                            data-test-selector="nextBtn"
                                        >
                                            <ButtonIcon src={ChevronRight} />
                                        </Button>
                                    )}
                                </GridItem>
                            </GridContainer>
                            {subExplodedViewToUse && (
                                <StyledWrapper {...styles.wrapper}>
                                    {!IS_SERVER_SIDE && (
                                        <React.Suspense
                                            fallback={
                                                <StyledWrapper {...styles.centeringWrapper}>
                                                    <LoadingSpinner />
                                                </StyledWrapper>
                                            }
                                        >
                                            <ExplodedViewNavigatorComponent product={subExplodedViewToUse} />
                                        </React.Suspense>
                                    )}
                                    <ExplodedViewItemsComponent product={subExplodedViewToUse} displayAsTable={true} />
                                </StyledWrapper>
                            )}
                        </>
                    )}
                    {evDrawings.length === 1 && evDrawingItemsList && evDrawingItemsList.length > 0 && (
                        <StyledWrapper {...styles.wrapper}>
                            {!IS_SERVER_SIDE && (
                                <React.Suspense
                                    fallback={
                                        <StyledWrapper {...styles.centeringWrapper}>
                                            <LoadingSpinner />
                                        </StyledWrapper>
                                    }
                                >
                                    <ExplodedViewNavigatorComponent product={evDrawings[0]} />
                                </React.Suspense>
                            )}
                            <ExplodedViewItemsComponent product={evDrawings[0]} displayAsTable={true} />
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

export default withIsInShell(withProduct(connect(mapStateToProps, mapDispatchToProps)(withTheme(ExplodedViewsList))));
